// Local civil date helpers.
//
// jobs.scheduled_date / manual_jobs.date are pure civil dates (YYYY-MM-DD with
// no timezone). Deriving "today" from new Date().toISOString() uses UTC, so
// after ~7pm America/New_York it returns TOMORROW's date and desyncs the whole
// app (today's completions vanish, tomorrow's stops appear on the route,
// current-day jobs flip to "Overdue"). Always derive civil dates locally.

// YYYY-MM-DD in the browser's local timezone.
export function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Parse a civil date string to a local noon Date (avoids DST/UTC edge shifts
// when only the calendar day matters). Mirrors the existing `+"T12:00:00"` trick.
export function civilDate(str) {
  return new Date(str + "T12:00:00");
}

// ── Scheduling preview ────────────────────────────────────────────────────────
// These MIRROR functions/_shared/schedule.ts. They exist so the Add-Client form can
// show the date a client will ACTUALLY first be serviced before you save. Keep them
// byte-faithful to the backend: a preview that disagrees with the generator is worse
// than no preview, because it gets believed. Both compute in UTC, like the backend.

const DAY_IDX = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

// ISO-8601 week number. Biweekly clients are split into two fortnights by the
// PARITY of this number, so it has to match _shared/schedule.ts:isoWeek exactly.
export function isoWeek(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = (d.getUTCDay() + 6) % 7;              // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - day + 3);           // Thursday of this week
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const ftDay = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - ftDay + 3);
  return 1 + Math.round((d.getTime() - firstThu.getTime()) / (7 * 864e5));
}

// Tolerant civil-date parser for spreadsheet input. Old CRMs export dates in whatever
// their locale felt like, so accept ISO and US M/D/Y (2-or-4-digit year). Returns
// YYYY-MM-DD, or null when the value isn't a date — never a guess.
export function toISODate(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  const pad = (n) => String(n).padStart(2, "0");
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    let y = m[3];
    if (y.length === 2) y = "20" + y;
    return `${y}-${pad(m[1])}-${pad(m[2])}`;   // US M/D/Y
  }
  return null;
}

// Which alternating fortnight a REAL service date fell on.
//
// This is the only reliable way to seed a biweekly client migrated from another system.
// Their start date tells you nothing — they've been served for months — and guessing is
// wrong 50% of the time, which puts them permanently a week out of phase with no
// self-correction. One real date from the old system settles it exactly.
export function parityFromDate(dateStr) {
  const iso = toISODate(dateStr);
  return iso ? (isoWeek(iso) % 2 === 0 ? "even" : "odd") : null;
}

// The first date on or after `fromStr` that this client would actually be serviced.
// Returns YYYY-MM-DD, or null when there's no service day to work from.
//
// `parity` empty/null = "auto", which is what create-customer's deriveBiweeklyParity
// does: it takes the FIRST occurrence of the service day on or after the start date
// and adopts that week's parity. So under auto, the first visit is simply the soonest
// matching weekday — the fortnight never pushes a new client a week out.
export function firstServiceDate(fromStr, dayName, frequency, secondDay = null, parity = null) {
  const primary = DAY_IDX[String(dayName || "").toLowerCase()];
  if (primary === undefined || !fromStr) return null;

  const wanted = [primary];
  if (frequency === "twice-weekly") {
    const s = DAY_IDX[String(secondDay || "").toLowerCase()];
    if (s !== undefined && s !== primary) wanted.push(s);
  }
  const wantParity = parity ? (String(parity).toLowerCase() === "odd" ? 1 : 0) : null;

  const d = new Date(fromStr + "T00:00:00Z");
  for (let i = 0; i < 400; i++) {                    // ~13 months, far past any real gap
    if (wanted.includes(d.getUTCDay())) {
      const iso = d.toISOString().split("T")[0];
      if (frequency !== "biweekly" || wantParity === null || isoWeek(iso) % 2 === wantParity) return iso;
    }
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return null;
}
